import {
  useAvailableActivities,
  useSendActivity,
} from "@/lib/api/activities";
import { usePostHogEvent } from "@/lib/posthog";
import type { ConnectQuestion } from "@somethings-ui/core/connect-questions";
import { useCallback, useState } from "react";
import { CONNECT_CARDSET_ID } from "./use-connect-rows";

interface UseConnectSendFlowOptions {
  mentorshipId: string;
  questions: { 1: ConnectQuestion; 2: ConnectQuestion; 3: ConnectQuestion };
  canSend: boolean;
  onSent: () => void;
}

interface ConnectSendFlow {
  isPending: boolean;
  error: string | null;
  onSend: () => void;
  sameDayConfirmOpen: boolean;
  confirmSameDay: () => void;
  dismissSameDay: () => void;
}

export function useConnectSendFlow({
  mentorshipId,
  questions,
  canSend,
  onSent,
}: UseConnectSendFlowOptions): ConnectSendFlow {
  const { data: availability } = useAvailableActivities(mentorshipId);
  const sendMutation = useSendActivity();
  const [error, setError] = useState<string | null>(null);
  const [sameDayConfirmOpen, setSameDayConfirmOpen] = useState(false);
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const performSend = useCallback(() => {
    setError(null);
    const questionIds = [questions[1].id, questions[2].id, questions[3].id];
    sendMutation.mutate(
      {
        mentorshipId,
        activityType: "connect_moment",
        config: { questionIds },
      },
      {
        onSuccess: () => {
          dispatchEvent("activity.mentor.sent", {
            mentorshipId,
            activityType: "connect_moment",
            cardsetId: CONNECT_CARDSET_ID,
            questionIds,
            conversationId: mentorshipId,
          });
          onSent();
        },
        onError: (
          err: Error & { response?: { data?: { error?: string } } },
        ) => {
          setError(
            err?.response?.data?.error ||
              "Couldn't send. Check your connection and try again.",
          );
        },
      },
    );
  }, [mentorshipId, questions, sendMutation, onSent, dispatchEvent]);

  const onSend = useCallback(() => {
    if (!canSend) return;
    if (availability?.sentToday) {
      setSameDayConfirmOpen(true);
      return;
    }
    performSend();
  }, [canSend, availability, performSend]);

  const confirmSameDay = useCallback(() => {
    setSameDayConfirmOpen(false);
    performSend();
  }, [performSend]);

  const dismissSameDay = useCallback(
    () => setSameDayConfirmOpen(false),
    [],
  );

  return {
    isPending: sendMutation.isPending,
    error,
    onSend,
    sameDayConfirmOpen,
    confirmSameDay,
    dismissSameDay,
  };
}
